<template>
  <b-card no-body header-tag="header" footer-tag="footer">
    <template slot="header">
      <div class="total-count mr-4">
        {{ totalRows }}
        <span class="label">Jobs found</span>
      </div>

      <!-- Search Box -->
      <div class="search-box" :class="{active: filter}">
        <div class="input-group">
          <div class="input-group-prepend">
            <div class="input-group-text">
              <font-awesome-icon icon="search" fixed-width class="icon" />
            </div>
          </div>
          <b-form-input
            type="text"
            class="form-control"
            v-model="filter"
            @keyup.native="searchJobs()"
            placeholder="Find a job by keyword..."
          />
          <button class="clear-search" @click="clearSearch()">
            <font-awesome-icon icon="times-circle" fixed-width class="icon" />
          </button>
        </div>
      </div>
    </template>
    <b-card-body>
      <!-- Per page selection -->
      <div class="form-inline per-page-selection d-flex align-items-center float-left">
        <b-form-select :options="pageOptions" v-model="perPage" @change="changePageSize" />
        <span class="ml-2 per-page-label">Jobs Per Page</span>
      </div>
      <div class="float-right">
        <button
          class="btn btn-sm btn-outline-secondary ml-2"
          v-if="totalRows < 1000 && totalRows > 0"
          @click="csvDownload"
        >
          <font-awesome-icon icon="file-export" class="icon" v-if="!downloading" />
          <font-awesome-icon icon="spinner" class="icon" spin v-if="downloading" />Export CSV
        </button>
      </div>
    </b-card-body>
    <!-- Data Table -->
    <b-table
      ref="jobsTable"
      show-empty
      striped
      responsive
      :busy.sync="loading"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :items="doJobsSearch"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      @filtered="onFiltered"
      empty-text="There are no jobs to display"
    >
      <div slot="table-busy" class="text-center text-danger my-2">
        <b-spinner class="align-middle" variant="primary"></b-spinner>
      </div>
      <template slot="top-row" slot-scope="data">
        <td v-for="field in data.fields" :key="field.filterKey" :class="field.tdClass">
          <input
            type="text"
            @keyup="updateColumnFilters()"
            class="form-control form-control-sm"
            v-if="field.filterType == 'text'"
            v-model="filters[field.filterKey]"
          />

          <b-form-select
            v-if="field.filterType == 'select'"
            v-model="filters[field.filterKey]"
            :options="field.filterOptions"
            @change="updateColumnFilters()"
            size="sm"
          />

          <input
            type="date"
            class="form-control form-control-sm"
            @change="updateColumnFilters()"
            v-if="field.filterType == 'date'"
            v-model="filters[field.filterKey]"
          />

          <input
            type="date"
            class="form-control form-control-sm"
            @change="updateColumnFilters()"
            v-if="field.filterType == 'date-range'"
            v-model="filters[field.filterKey][0]"
            style="width: 90%;"
          />
          <input
            type="date"
            class="form-control form-control-sm"
            @change="updateColumnFilters()"
            v-if="field.filterType == 'date-range'"
            v-model="filters[field.filterKey][1]"
            style="width: 90%;"
          />
        </td>
      </template>
      <!-- Status Badge -->
      <template slot="status" slot-scope="row">
        <span
          class="badge badge-md"
          :class="'badge-'+row.value.toLowerCase().trim().replace(/ /g,'-')"
        >{{ row.value }}</span>
      </template>

      <!-- Lab Ref -->
      <template slot="labRegistrationReference" slot-scope="data">
        <router-link tag="a" :to="`/jobs/${ data.item.jobId }`">
          <strong>
            {{ data.item.labRegistrationReference }}
            <font-awesome-icon icon="angle-right" fixed-width class="icon" />
          </strong>
        </router-link>
      </template>

      <!-- Receive Date -->
      <template slot="receivedDate" slot-scope="row">{{ row.value | moment("D MMM YYYY") }}</template>

      <!-- DueOrFinalised -->
      <template slot="dueOrFinalisedDate" slot-scope="row">{{ row.value | moment("D MMM YYYY") }}</template>
    </b-table>

    <!-- Footer -->
    <template slot="footer">
      <div class="d-flex align-items-center justify-content-between">
        <span>
          <span v-if="totalPages > 0">Page {{ currentPage }} of {{ totalPages }}</span>
        </span>
        <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" />
      </div>
    </template>
  </b-card>
</template>

<style lang="scss">
@import "../assets/styles/main.scss";
</style>

<script>
import DropdownFilter from "@/components/DropdownFilter.vue";
import SelectedFilters from "@/components/SelectedFilters.vue";
import { EventBus } from "@/services/event-bus.js";
import { searchJobs } from "@/services/api";
import { getJobSort, storeJobSort } from "@/services/localstorage";
import _ from "lodash";
const moment = require("moment");
const Json2csvParser = require("json2csv").Parser;

export default {
  name: "JobsTable",
  components: { DropdownFilter, SelectedFilters },
  props: {
    selectedClients: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      fields: [
        {
          key: "status",
          sortable: true,
          filterType: "select",
          filterKey: "status",
          filterOptions: [
            { text: "All", value: null },
            { text: "Registered", value: "Registered" },
            { text: "Complete", value: "Complete" },
            { text: "Results Available", value: "Results Available" }
          ]
        },
        {
          key: "labRegistrationReference",
          label: "Lab Reference",
          sortable: true,
          filterType: "text",
          filterKey: "labRegistrationReference"
        },
        {
          key: "clientRegistrationReference",
          label: "Client Reference",
          sortable: true,
          filterType: "text",
          filterKey: "clientRegistrationReference"
        },
        {
          key: "clientOrderNumber",
          label: "Purchase Order",
          sortable: true,
          filterType: "text",
          filterKey: "clientOrderNumber"
        },
        {
          key: "numberOfSamples",
          label: "Samples",
          sortable: true
        },
        {
          key: "receivedDate",
          label: "Received",
          sortable: true,
          filterType: "date-range",
          filterKey: "receivedDate"
        },
        {
          key: "dueOrFinalisedDate",
          label: "Due/Finalised",
          sortable: true,
          filterType: "date-range",
          filterKey: "dueOrFinalisedDate"
        },
        {
          key: "priority",
          sortable: true,
          filterType: "select",
          filterKey: "priority",
          filterOptions: [
            { text: "All", value: null },
            { text: "Urgent", value: "Urgent" },
            { text: "Priority", value: "Priority" },
            { text: "Normal", value: "Normal" }
          ]
        }
      ],
      filters: {
        status: null,
        priority: null,
        labRegistrationReference: "",
        registrationDescription: "",
        clientOrderNumber: "",
        receivedDate: ["", ""],
        dueOrFinalisedDate: ["", ""]
      },
      currentPage: 1,
      totalPages: 1,
      perPage: 20,
      totalRows: 0,
      pageOptions: [20, 50, 100, 500],
      filter: null,
      downloading: false,
      priorityFilters: [],
      statusFilters: [],
      loading: false,
      timeout: null,
      disablethis: true,
      sortBy: "dueOrFinalisedDate",
      sortDesc: true
    };
  },
  watch: {
    loading() {
      if (this.loading) {
        EventBus.$emit("showLoader");
      } else {
        EventBus.$emit("hideLoader");
      }
    },
    selectedClients: function() {
      this.refreshTable();
    }
  },
  methods: {
    changePageSize(value) {
      this.currentPage = 1;
      this.perPage = value;
      this.refreshTable();
    },
    changePage(page) {
      this.currentPage = page;
      this.refreshTable();
    },
    updateColumnFilters() {
      this.currentPage = 1;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.refreshTable();
      }, 500);
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    clearSearch() {
      this.filter = "";
      this.refreshTable();
    },
    searchJobs() {
      this.currentPage = 1;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (this.filter.length < 1) {
          this.clearSearch();
        }
        this.refreshTable();
      }, 500);
    },
    forceFileDownload(response, filename) {
      const blob = new Blob([response]);

      //for microsoft IE
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // no filename in response :(
        link.setAttribute("download", filename); // have to specify a filename here
        document.body.appendChild(link);
        this.downloading = false;
        link.click();
      }
    },
    async csvDownload() {
      this.downloading = true;
      const promises = [];
      const paramsTemplate = this.makeParams();
      const pages = Math.ceil(this.totalRows / 500);
      for (let i = 0; i <= pages; i++) {
        const params = JSON.parse(JSON.stringify(paramsTemplate));
        params.page = i + 1;
        params.pageSize = 500;
        promises.push(searchJobs(params));
      }
      const results = await Promise.all(promises);
      const rows = results.reduce((accumulator, resultSet) => {
        resultSet.jobs.forEach(job => {
          Object.keys(job.client).forEach(clientKey => {
            job[`client.${clientKey}`] = job.client[clientKey];
          });
          delete job.client;
          Object.keys(job.invoiceTo).forEach(invoiceToKey => {
            job[`invoiceTo.${invoiceToKey}`] = job.invoiceTo[invoiceToKey];
          });
          delete job.invoiceTo;
          accumulator.push(job);
        });
        return accumulator;
      }, []);
      const fields = Object.keys(rows[0]);
      try {
        const opts = { fields };
        const parser = new Json2csvParser(opts);
        const csv = parser.parse(rows);
        this.forceFileDownload(
          csv,
          `analytica_jobs_search_exported_${moment().format(
            "YYYY_MM_DD_HH_mm_ss"
          )}.csv`
        );
      } catch (err) {
        console.error(err);
      }

      this.downloading = false;
    },
    makeParams() {
      const params = {
        page: this.currentPage,
        pageSize: this.perPage,
        orderFields: [
          {
            field: this.sortBy,
            direction: this.sortDesc ? "DESC" : "ASC"
          }
        ],
        searchFields: {}
      };

      // text search
      if (this.filter && this.filter.trim() !== "") {
        params.textSearch = this.filter;
      }

      // client selection
      if (this.selectedClients.length > 0)
        params.searchFields.clients = this.selectedClients.map(
          client => client.id
        );

      // Column Filters
      this.fields.forEach(field => {
        let key = field.filterKey;
        if (field.filterType === "text") {
          if (this.filters[key] && this.filters[key].trim())
            params.searchFields[key] = this.filters[key].trim();
        } else if (field.filterType === "date") {
          if (this.filters[key])
            params.searchFields[key] = [
              {
                value: this.filters[key],
                operator: "="
              }
            ];
        } else if (field.filterType === "select") {
          if (this.filters[key]) params.searchFields[key] = this.filters[key];
        } else if (
          field.filterType === "date-range" ||
          field.filterType === "number"
        ) {
          if (this.filters[key][0]) {
            params.searchFields[key] = [
              {
                value: this.filters[key][0],
                operator: ">="
              }
            ];
          }
          if (this.filters[key][1]) {
            if (!params.searchFields[key]) params.searchFields[key] = [];
            params.searchFields[key].push({
              value: this.filters[key][1],
              operator: "<="
            });
            // we need to track where this came from, so
            if (params.searchFields[key].length === 1) {
              params.searchFields[key][0].isFirst = true;
            }
          }
        }
      });

      return params;
    },
    async doJobsSearch(ctx) {
      let params = this.makeParams();
      storeJobSort(params);
      params = this.filterMarkup(params);

      const result = await searchJobs(params);

      this.perPage = result.pagination.pageSize;
      this.totalRows = result.pagination.totalResults;
      this.totalPages = Math.ceil(this.totalRows / this.perPage);

      return result.jobs;
    },
    restoreParams(config) {
      // always setting this to one - pagination won't set it properly for some reason.
      this.currentPage = 1; //config.page || 1;
      this.perPage = config.pageSize || 20;
      this.filter = config.textSearch || "";

      if (config.orderFields && config.orderFields.length > 0) {
        if (config.orderFields[0].field) {
          this.sortBy = config.orderFields[0].field;
          this.sortDesc = true;
          if (config.orderFields[0].direction !== "DESC") {
            this.sortDesc = false;
          }
        }
      }

      // now the column filters - a wee bit trickier
      if (config.searchFields) {
        const keys = Object.keys(config.searchFields);
        keys.forEach(key => {
          // find the object in the fields array
          let field = null;
          this.fields.forEach(f => {
            if (f.filterKey === key) {
              field = f;
            }
          });

          if (field) {
            if (field.filterType) {
              switch (field.filterType) {
                case "number":
                case "date-range":
                  if (config.searchFields[key].length > 0) {
                    this.filters[key][0] = config.searchFields[key][0].value;

                    if (config.searchFields[key][0].isFirst) {
                      this.filters[key][0] = "";
                      this.filters[key][1] = config.searchFields[key][0].value;
                      break;
                    }
                  } else {
                    this.filters[key][0] = "";
                  }

                  if (config.searchFields[key].length > 1) {
                    this.filters[key][1] = config.searchFields[key][1].value;
                  } else {
                    this.filters[key][1] = "";
                  }
                  break;
                case "date":
                  this.filters[key] = config.searchFields[key].value;
                  break;
                case "select":
                case "text":
                default:
                  this.filters[key] = config.searchFields[key];
                  break;
              }
            } else {
              this.filters[key] = config.searchFields[key];
            }
          }
        });
      }
    },
    filterMarkup(params) {
      // Have to add some marker fields to the params in order to be able to restore them,
      // and this then removes those fields so that the search does not fail.
      this.fields.forEach(field => {
        let key = field.filterKey;
        if (Array.isArray(params.searchFields[key])) {
          if (params.searchFields[key].length > 0) {
            for (let i = 0; i < params.searchFields[key].length; i++) {
              let p = params.searchFields[key][i];
              if (p.isFirst) {
                delete p.isFirst;
              }
            }
          }
        }
      });

      return params;
    },
    refreshTable() {
      this.$refs.jobsTable.refresh();
    }
  },
  mounted() {
    const sortConfig = getJobSort();
    if (sortConfig) {
      this.restoreParams(sortConfig);
    }
    this.refreshTable();
  }
};
</script>



// WEBPACK FOOTER //
// src/components/JobsTable.vue